// Hooks
import { useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import { notifications } from "@mantine/notifications";
import { showErrorNotification } from "@benchmark/ui/utils";

// Types
import type { Patient } from "@medplum/fhirtypes";
import type { PatientEngagementStatusCode } from "@benchmark/core/constants";

// Utils
import { getPatientEngagementStatus } from "@benchmark/core/resolvers";
import {
  updatePatientEngagementStatus,
  migratePatientEngagementStatus,
} from "@benchmark/core/actions";

/**
 * Result type for useEngagementStatus hook.
 * @agentic connections
 */
export type UseEngagementStatusResult = {
  currentStatus: ReturnType<typeof getPatientEngagementStatus>;
  isUpdating: boolean;
  error: Error | null;
  updateStatus: (
    newStatus: PatientEngagementStatusCode,
    reason?: string,
  ) => Promise<Patient | null>;
  migrateToV2: () => Promise<Patient | null>;
};

/**
 * Manages patient engagement status: reads current status, updates via Patient tags, migrates to V2.
 * @agentic connections
 */
export function useEngagementStatus(
  patient: Patient | undefined,
  onSuccess?: () => void,
): UseEngagementStatusResult {
  const medplum = useMedplum();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const currentStatus = patient ? getPatientEngagementStatus(patient) : null;

  const updateStatus = useCallback(
    async (
      newStatus: PatientEngagementStatusCode,
      reason?: string,
    ): Promise<Patient | null> => {
      if (!patient) {
        return null;
      }

      setIsUpdating(true);
      setError(null);

      try {
        const updatedPatient = await updatePatientEngagementStatus(
          medplum,
          patient,
          newStatus,
          reason,
        );

        notifications.show({
          title: "Status Updated",
          message: `Patient engagement status updated to ${newStatus}`,
          color: "green",
        });

        onSuccess?.();
        return updatedPatient;
      } catch (err) {
        console.error("Error updating engagement status:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update status";
        // Wrap non-Error exceptions in Error object for consistent error handling
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);

        showErrorNotification(errorMessage, { title: "Update Failed" });

        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [patient, medplum, onSuccess],
  );

  const migrateToV2 = useCallback(async (): Promise<Patient | null> => {
    if (!patient) {
      return null;
    }

    setIsUpdating(true);
    setError(null);

    try {
      const updatedPatient = await migratePatientEngagementStatus(
        medplum,
        patient,
      );

      notifications.show({
        title: "Status Migrated",
        message: "Patient engagement status migrated to V2",
        color: "blue",
      });

      onSuccess?.();
      return updatedPatient;
    } catch (err) {
      console.error("Error migrating engagement status:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to migrate status";
      // Wrap non-Error exceptions in Error object for consistent error handling
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);

      showErrorNotification(errorMessage, { title: "Migration Failed" });

      return null;
    } finally {
      setIsUpdating(false);
    }
  }, [patient, medplum, onSuccess]);

  return {
    currentStatus,
    isUpdating,
    error,
    updateStatus,
    migrateToV2,
  };
}
